import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "../lib/data";
import { api } from "../lib/api";
import LazyImage from "./LazyImage";

const CATEGORY_ALT = {
    Bodas: "Reportaje de boda en Bilbao y País Vasco — Silvia García Fotografía",
    Cumpleaños:
        "Sesión de cumpleaños en Sestao — Silvia García Fotografía",
    Comuniones:
        "Reportaje de comunión en Barakaldo y Portugalete — Silvia García",
    Eventos: "Fotografía de eventos en Bilbao — Silvia García Fotografía",
    "Sesiones exteriores":
        "Sesión exterior en el País Vasco — Silvia García Fotografía",
    Estudio: "Sesión de estudio en Sestao — Silvia García Fotografía",
};
const altFor = (category) =>
    CATEGORY_ALT[category] ||
    `Fotografía de ${category || ""} — Silvia García Fotografía`;

/**
 * Public Portfolio section.
 *
 * The homepage section is intentionally compact: only the title, subtitle
 * and a big CTA. The full portfolio grid + filters opens in a fullscreen
 * window (modal). This keeps the home short to scroll. The same modal is
 * triggered by the navbar "Portfolio" link and the Hero "Ver mi trabajo"
 * button through a `portfolio:open` window event.
 */
export default function Portfolio() {
    const [filter, setFilter] = useState("Todo");
    const [items, setItems] = useState(PORTFOLIO);
    const [windowOpen, setWindowOpen] = useState(false);
    const [lightboxIdx, setLightboxIdx] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchItems = () => {
            api.get(`/portfolio?t=${Date.now()}`)
                .then(({ data }) => {
                    if (mounted && Array.isArray(data)) {
                        setItems(data);
                    }
                })
                .catch(() => {});
        };
        fetchItems();
        const onVis = () => {
            if (document.visibilityState === "visible") fetchItems();
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", fetchItems);
        return () => {
            mounted = false;
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", fetchItems);
        };
    }, []);

    // Listen for global "open portfolio" requests (from Navbar / Hero).
    useEffect(() => {
        const handler = () => setWindowOpen(true);
        window.addEventListener("portfolio:open", handler);
        return () => window.removeEventListener("portfolio:open", handler);
    }, []);

    // Lock body scroll while window is open.
    useEffect(() => {
        if (windowOpen || lightboxIdx !== null) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
    }, [windowOpen, lightboxIdx]);

    const filtered = useMemo(() => {
        if (filter === "Todo") return items;
        return items.filter((p) => p.category === filter);
    }, [filter, items]);

    const openLightbox = (idx) => setLightboxIdx(idx);
    const closeLightbox = () => setLightboxIdx(null);
    const next = () =>
        setLightboxIdx((i) => (i === null ? 0 : (i + 1) % filtered.length));
    const prev = () =>
        setLightboxIdx((i) =>
            i === null ? 0 : (i - 1 + filtered.length) % filtered.length,
        );

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="relative py-14 sm:py-20 bg-[#FAFAF8]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-center max-w-3xl mx-auto reveal">
                    <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                        Portfolio
                    </span>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1C1A18] mt-4 leading-[1.1]"
                        data-testid="portfolio-title"
                    >
                        Historias que he tenido el honor de contar
                    </h2>
                    <p
                        className="mt-6 text-[#6b665f] font-light"
                        data-testid="portfolio-subtitle"
                    >
                        Cada imagen es un momento real. Sin poses forzadas, sin
                        artificios. Solo luz, emoción y honestidad.
                    </p>
                </div>

                <div className="mt-10 sm:mt-14 flex justify-center reveal reveal-delay-1">
                    <button
                        type="button"
                        onClick={() => setWindowOpen(true)}
                        data-testid="portfolio-open-window-btn"
                        className="group relative inline-flex items-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#1C1A18] text-[#FAFAF8] rounded-full overflow-hidden transition-all duration-500 hover:bg-[#C4A882] hover:text-[#1C1A18] active:scale-95 shadow-2xl"
                    >
                        <Sparkles
                            size={20}
                            strokeWidth={1.2}
                            className="text-[#C4A882] group-hover:text-[#1C1A18] transition-colors"
                        />
                        <span className="flex flex-col items-start text-left">
                            <span className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase opacity-70">
                                Descubre
                            </span>
                            <span className="font-display text-xl sm:text-2xl tracking-wide leading-none mt-1">
                                Ver trabajos realizados
                            </span>
                        </span>
                    </button>
                </div>

                <p className="mt-5 text-center text-[10px] tracking-[0.32em] uppercase text-[#9a958c]">
                    {items.length} fotografía{items.length === 1 ? "" : "s"}{" "}
                    disponibles
                </p>
            </div>

            {windowOpen && (
                <PortfolioWindow
                    items={items}
                    filter={filter}
                    setFilter={setFilter}
                    filtered={filtered}
                    onClose={() => setWindowOpen(false)}
                    onOpenLightbox={openLightbox}
                />
            )}

            {lightboxIdx !== null && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="portfolio-lightbox"
                    className="fixed inset-0 z-[120] bg-[#1C1A18]/95 flex items-center justify-center p-4"
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            closeLightbox();
                        }}
                        aria-label="Cerrar"
                        data-testid="lightbox-close"
                        className="absolute top-6 right-6 text-white p-2"
                    >
                        <X size={28} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        aria-label="Anterior"
                        data-testid="lightbox-prev"
                        className="absolute left-4 sm:left-10 text-white p-2"
                    >
                        <ChevronLeft size={36} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        aria-label="Siguiente"
                        data-testid="lightbox-next"
                        className="absolute right-4 sm:right-10 text-white p-2"
                    >
                        <ChevronRight size={36} />
                    </button>

                    <div
                        className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <LazyImage
                            src={
                                filtered[lightboxIdx].image ||
                                filtered[lightboxIdx].url
                            }
                            alt={altFor(filtered[lightboxIdx].category)}
                            eager
                            className="max-h-[80vh] w-auto object-contain"
                            data-testid="lightbox-image"
                        />
                        <span className="mt-4 text-white/70 text-xs tracking-[0.4em] uppercase">
                            {filtered[lightboxIdx].category}
                        </span>
                    </div>
                </div>
            )}
        </section>
    );
}

function PortfolioWindow({
    items,
    filter,
    setFilter,
    filtered,
    onClose,
    onOpenLightbox,
}) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="portfolio-window"
            className="fixed inset-0 z-[100] bg-[#FAFAF8] flex flex-col animate-fadein"
        >
            {/* Header (sticky) */}
            <div className="sticky top-0 z-10 bg-[#FAFAF8]/95 backdrop-blur-md border-b border-[#e6e2db]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-4">
                    <div>
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                            Portfolio
                        </span>
                        <h3 className="font-display text-2xl sm:text-3xl text-[#1C1A18] mt-1 leading-none">
                            Trabajos realizados
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Cerrar"
                        data-testid="portfolio-window-close"
                        className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-[#1C1A18]/15 bg-white hover:bg-[#1C1A18] hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-4 flex flex-wrap gap-x-6 gap-y-2">
                    {PORTFOLIO_CATEGORIES.map((cat) => (
                        <button
                            type="button"
                            key={cat}
                            onClick={() => setFilter(cat)}
                            data-testid={`portfolio-window-filter-${cat.toLowerCase()}`}
                            className={`chip ${filter === cat ? "active" : ""}`}
                        >
                            {cat}{" "}
                            <span className="ml-1 text-[#C4A882]">
                                (
                                {cat === "Todo"
                                    ? items.length
                                    : items.filter((i) => i.category === cat)
                                          .length}
                                )
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Grid (scrollable) */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
                    {filtered.length === 0 ? (
                        <div className="text-center text-[#6b665f] font-light text-sm py-20">
                            Aún no hay fotos en esta categoría.
                        </div>
                    ) : (
                        <div
                            key={filter}
                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px] lg:auto-rows-[260px] portfolio-grid-fade"
                            data-testid="portfolio-window-grid"
                        >
                            {filtered.map((img, idx) => {
                                const spanClass =
                                    img.span === "tall"
                                        ? "row-span-2"
                                        : img.span === "wide"
                                          ? "col-span-2"
                                          : "";
                                return (
                                    <button
                                        type="button"
                                        key={img.id}
                                        className={`photo-card ${spanClass}`}
                                        onClick={() => onOpenLightbox(idx)}
                                        data-testid={`portfolio-window-item-${img.id}`}
                                        aria-label={`Ver foto ${img.category}`}
                                    >
                                        <LazyImage
                                            src={img.image || img.url}
                                            alt={altFor(img.category)}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="photo-overlay">
                                            <span className="text-white text-xs tracking-[0.4em] uppercase font-light">
                                                {img.category}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
